import { CasingPlanModel, WaterSource } from "../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { WellLocation, WellType, Well } from "./well.model";
import { LegacyPermitPayment } from "../../payments/models/paymentItem.model";
import { Address } from "./address.model";

export class PermitSearchSpec {

    hasAPINumber: boolean = false;
    ids: string[] = [];
    organizationId: string = "";
    apiNumber: string = "";
    cancellationStatus: boolean = false;
    //properties below are for limiting the sub-data sets to be included in the result.  only include those sets necessary for the purpose
    includeProposedWell: boolean = true;
    includeRestorationPlan: boolean = true;
    includeCasingPlan: boolean = true;
    includeAccessRoutes: boolean = true;
    includeContacts: boolean = true;
    includeOtherWells: boolean = true;
    includeRUMAs: boolean = true;
    includeRoyaltyInterests: boolean = true;
    includeSectionApprovals: boolean = true;
    includeWaterSources: boolean = true;
    includeLocationGISPoints: boolean = true;

    constructor() { }
}

export class Permit {
    id: string;
    apiNumber: string;
    type: string;
    applicationNumber: string;
    applicationReceivedDate: Date;
    county: string;
    permitType: PermitType;   
    wellType: string;
    expeditedStatus: Expedite;
    payment: LegacyPermitPayment;
    varianceType: string;
    poolAmount: number;
    organizationId: string;
    organizationName: string;
    chiefsOrder: PermitDeniedByChiefsOrder;
    companyRepresentativeNumber: string;
    facility: Facility;
    permitMailing: PermitMailing;
    isExistingWell: boolean;
    wellName: string;
    wellNumber: string;
    drillingUnitAcres: number;
    rigType: string;
    officialContacts: OfficialContact[];
    ingress: AccessRoute;
    egress: AccessRoute;
    alternateIngress: AccessRoute;
    alternateEgress: AccessRoute;
    division: string;
    divisionPhoneNumber: string;
    proposal: ProposedWell;
    haulerNumbers: string;
    wellHeadElevation: number;
    targetSurfaceElevation: number;
    applicationProcessing: ApplicationProcessing;
    protests: Protest;
    isCancelled: boolean;
    dateCancelled: Date;
    dateOfExpiration: Date;
    dateIssued: Date;
    divisionOfOilAndGasInspectorNumber: string;
    divisionOfMinesInspectorNumber: string;
    userId: string;
    comment: string;
    dateModified: Date;
    urbanizedArea: UrbanizedArea;
    mpCheckNumber: number;
    isUticaShaleWell: boolean;
    isMarcellusShaleWell: boolean;
    waterSources: PermitWaterSource[];
    roadMaintenanceAgreement: RoadMaintenanceAgreement;
    alternateRoadMaintenanceAgreement: RoadMaintenanceAgreement;
    waterWellSampling: WaterWellSampling;
    stateOwnedLand: boolean;
    stateRouteNotRequired: boolean;
    location: WellLocation;
    restorationplan: RestorationPlan;
    specialConditions: string[];
    confidentialityClaimed: boolean;
    landOwnerAffidavit: boolean;
    royaltyInterests: RoyaltyInterest[];
    surfaceRights: SurfaceRight;
    natureOfBusiness: string;
    otherPermits: string;
    observationsWells: WellInfo[];
    withdrawalWells: WellInfo[];
    fluidMigrationPlan: string;
    contingencyPlan: string;
    monumentGrid: string;
    monitoringRequirements: MonitoringRequirements;
    surfaceFacility: SurfaceFacility;
    unloadingPaid: string;
    casingPlan: CasingPlanModel;
    organizationSignature: string;
    chiefsSignature: string;
    inspectors: CountyInspector;
    areaOfReviewCorrectiveAction: AreaOfReviewCorrectiveAction;

    subjectToMandatoryPoolingOrUnitizationOrder: string;
    unitizationChiefsOrder: UnitizationChiefsOrder;
    medicalServicePhoneNumber?: string;
    firePhoneNumber?: string;
}


export interface MonitoringRequirements {
    injectionFluidSample: string;
    injectionPressure: string;
    injectionZone: string;
    irregularConditions: string;
    method: string;
}

export interface WellInfo {
    APINumber: string;
    ownerName: string;
    ownerInfo: string;
    formation: string;
    latitude?: number;
    longitude?: number;
    producingFormation: string;
}

export interface RoyaltyInterest {
    isState: boolean;
    agencyName: string;
    owner: Owner;
}


export interface Owner {
    name: boolean;
    address: Address;
}

export interface RestorationPlan {
    landeUse: string[],
    measurement: string[],
    gradientOther: string,
    fallVegetalCover: string[],
    resoilingMaterial: string[],
    resoilingAlternative: string,
    disposalOfTreeStumps: string[],
    disposalTreeAlternative: string,
    drainageFacilities: string[],
    steepestSlopeCrossingSite: string[],
    steepestSlopeCrossingSiteLength: string[],
    drillingPits: string[],
    drillingPitsAlternative: string,
    backfill: string[],
    backfillingAlternative: string,
    vegetativeCover: string[],
    vegetativeCoverAlternative: string,
    additionalholes: string[],
    accessRoadLength: string[],
    accessRoadLanduse: string[],
    accessRoadSurfacingMaterial: string[],
    surfacingMaterialAlternative: string,
    accessRoadPathDeterminedBy: string[],
    erosionControl: string[],
    erosionControlAlternative: string,
    steepestSlopeAccessRoad: string[],
    steepestSlopeAccessRoadLength: string,
    landownerReceivedPlan: boolean
}

export interface WaterWellSampling {
    resultsReceived: boolean;
    dateSampled: Date;
}

export interface RoadMaintenanceAgreement {
    wasReached: boolean;
    goodFaithAttemptAffidavitSupplied: boolean;
    stateRoutesOnly: boolean;
}

export interface PermitWaterSource extends WaterSource {
    isLakeErieSource: boolean;
    isOhioRiverSource: boolean;
}

export interface UrbanizedArea {
    isUrban: boolean;
    confirmedNotUrban: boolean;
    name: string;
}

export interface Protest {
    dateOfHearing: Date;
    dateSentToLegal: Date;
    wasProtested: boolean;
    wentToHearing: boolean;
}

export interface PermitDeniedByChiefsOrder {
    chiefOrderNumber: string;
    dateApplicationDenied: Date;
}

export interface UnitizationChiefsOrder {
    chiefOrderNumber: string;
    dateChiefsOrderIssued: Date;
}

export interface ApplicationProcessing {
    dateAffidavitReceived: Date;
    dateAffidavitApproved: Date;
    neededCorrections: Corrections;
    UICSection: SectionApproval;
    GeologySection: SectionApproval;
    MinesDivision: SectionApproval;
}

export interface Corrections {
    applicantNotifiedNeededCorrections: boolean;
    dateApplicantNotifiedOfNeededCorrections: Date;
    dateCorrectionsApproved: Date;
}

export interface SectionApproval {
    dateSent: Date;
    initialsOfProcessor: string;
    dateApproved: Date;
}

export interface ProposedFormation {
    name: string;
    onlyThroughAnd: number;
}

export interface InjectionZone {
    topFromGroundLevel: number;
    bottomFromGroundLevel: number;
    maximumPressure: string;
    description: string;
    upperPerforation: number;
    lowerPerforation: number;
}

export interface ProposedWell {
    wellType: WellType;
    totalDepth: number;
    wellClass: string;
    formations: ProposedFormation[];
    willBePlugged: boolean;
    construction: string[];
    casingTesting: string;
    injectionZone: InjectionZone;
    stimulationProgram: string;
    proposedInjectionFluid: string;
    maximumDailyInjectionVolume: number;
    averageDailyInjectionVolume: number;
    maximumDailyInjectionPressure: number;
    averageDailyInjectionPressure: number;

    fracturePressureOfFormation: number;
    fracturePressureDeterminationMethod: string;
    depthOfBaseOfTargetFormation: number;
    isEntireWellboreInProposedUnit: boolean;
    hasPermissionToDrillOutsideUnit: boolean;
}

export interface SurfaceRight {
    stateOwnsRights: boolean;
    agencies: string[];
}

export interface OfficialContact {
    contactType: string;
    companyName: string;
    contactName: string;
    contactTitle: string;
    phone: string;
    email: string;
}

export interface PermitMailing {
    isSeparateMailingAddressRequired: boolean;
    contact: string;
    address: Address;
    phoneNumber: string;
}

export interface Facility {
    address: Address;
}

export interface Operator {
    FirstName: string;
    MiddleInitial: string;
    LastName: string;
    Suffix: string;
    address: Address;
    email: string;
    primaryPhone: string;
    secondaryPhone: string;
    fax: string;
}

export interface Expedite {
    expedited: boolean;
    checkNumber: number;
    checkAmount: number;
}

export enum PermitType {
    Drill,
    Deepen,
    Reopen,
    Plug,
    BrineHauler
}

export enum ProposedWellType {
    OilAndGas,
    GasStorage,
    WaterSupply,
    CoalBedMethane,
    SolutionMining,
    SaltwaterInjection,
    EnhancedRecovery
}


export enum WorkOrderStatus {
    Pending,
    Waiting,
    Returned,
    Completed,
    Cancelled
}

export interface CountyInspector {    
    backupInspectorName: string,
    backupInspectorPhone: string,
    countyName: string,
    countyNumber: number,
    inspectorName: string,
    inspectorPhone: string,
    regionalPhone: string,
    rowKey: string,
    supervisorName: string,
    supervisorPhone: string,
}

export class InspectorListSearchCriteria {
    countyCode: string;
    countyName: string;

    constructor(countyCode: string, countyName: string) {
        this.countyCode = countyCode;
        this.countyName = countyName;
    }
}

export interface AccessRoute {
    path: string;
    points: Point[],
    entryPointLatitude?: number,
    entryPointLongitude?: number
}

export interface Point {
    latitude: number;
    longitude: number;
}

export interface AreaOfReviewCorrectiveAction {
    action: string;
    wells: WellInfo[];
}

export interface SurfaceFacility {
    proposed: string,
    description: string
}

export interface RegeneratePermitRequest {
    id: string;
    schematic: string;
}

export interface RegeneratedPermitResult {
    id: string;
    data: any;
    documentId?: string;
    schematic?: string;
    uploadStatus: DocumentUploadStatus;
}

export enum DocumentUploadStatus {
    Uploading = "Uploading",
    Complete = "Complete",
    Error = "Error"
}

export enum LandownerProgramStatus {
    NotFound = "Well Not Found",
    NotEnrolled = "Not Enrolled",
    Active = "Active",
    Plugged = "Plugged",
    Withdrawn = "Withdrawn",
    IneligibleDisposition = "Ineligible by Disposition",
    InProcess = "In Process",
    AwaitingReview = "Awaiting Review",
    ReturnedForCorrection = "Returned for Correction"
}

export enum LandownerProgramEvents {
    Created = "Application Created",
    ReturnedForCorrection = "Application Returned for Corrections",
    Approved = "Application Approved",
    Withdrawn = "Withdrawn from Program",
    Updated = "Application Updated",
    Submitted = "Application Submitted for Approval",
    Plugged = "Well Plugged"
}

export interface LandownerProgramStatusResponse {
    lastUpdatedDate: Date;
    startDate: Date;
    status: LandownerProgramStatus;
    well: Well;
    relatedPermits: Permit[];
    inspectorList?: CountyInspector;
}

export interface LandownerPassthroughProgramDecision {
    application: LandownerPassthroughProgramApplication;
    statusChange: LandownerProgramStatus;
}

export interface PassthroughProgramContractor {
    applicationId?: string;
    contractorName: string;
    contractorRepresentative: string;
    streetAddress: string;
    suite: string;
    postOfficeBox: string;
    city: string;
    stateCode: string;
    zipCode: string;
    officePhone: string;
    cellPhone: string;
    emailAddress: string;
}

export interface PassthroughProgramEmergencyData {
    applicationId?: string;
    firePhoneNumber: string;
    fireExtension: string;
    medicalPhoneNumber: string;
    medicalExtension: string;
    meansOfIngress: string;
}

export interface PassthroughProgramLandowner {
    applicationId?: string;
    ownerName: string;
    streetAddress: string;
    suite: string;
    postOfficeBox: string;
    city: string;
    stateCode: string;
    zipCode: string;
    contactPhone: string;
    emailAddress: string;
}

export interface PassthroughProgramInspector {
    applicationId?: string;
    inspectorName: string;
    inspectorPhone: string;
    backupInspectorName: string;
    backupInspectorPhone: string;
    supervisorName: string;
    supervisorPhone: string;
}

export interface PassthroughProgramWell {
    applicationId?: string;
    wellId: string;
    latitude?: number;
    longitude?: number;
    county: string;
    township: string;
    section: string;
    lot: string;
    tract: string;
    urbanized?: boolean;
    wellName: string;
    wellNumber: string;
    lastOwner: string;
    dateOfLastProduction: Date;
    amountOfLastOil?: number;
    amountOfLastGas?: number;
    depthToBePlugged?: number;
    waterSource: string;
    watershed: string;
    amountWater?: number;
}

export interface PassthroughProgramFile {
    queuedDocumentId?: string;
    applicationId?: string;
    fileName: string;
    programArea: string;
    documentType: string;
    documentGroup: string;
    documentUri: string;
    contentType: string;
    id?: number;
}

export interface LandownerPassthroughProgramHistory {
    id: number;
    applicationId: string;
    recordedById: string;
    recordedByName: string;
    recordedDate: Date;
    eventTypeId: number;
    eventTypeDescription: string;
}

export interface LandownerPassthroughProgramNote {
    id?: number;
    applicationId: string;
    comment: string;
    createdByUserId?: string;
    createdByUserName?: string;
    dateCreated?: Date;
}


export interface LandownerPassthroughProgramApplication {
    applicationId?: string;
    wellId: string;
    programStatus?: LandownerProgramStatus;
    dateCreated?: Date;
    lastUpdated?: Date;
    lastUpdatedByUserId: string;
    lastUpdatedByUserName: string;
    contractorEstimate?: number;
    createdByUserId?: string;
    createdByUserName?: string;

    passthroughProgramContractor: PassthroughProgramContractor;
    passthroughProgramEmergencyData: PassthroughProgramEmergencyData;
    passthroughProgramInspector: PassthroughProgramInspector;
    passthroughProgramLandowner: PassthroughProgramLandowner;
    passthroughProgramWell: PassthroughProgramWell;
    passthroughProgramFiles: PassthroughProgramFile[];
    landownerPassthroughProgramHistories: LandownerPassthroughProgramHistory[];
    landownerPassthroughProgramNotes: LandownerPassthroughProgramNote[];
}



export class LandownerProgramApplicationResponse {
    applicationId: string;
    queuedDocumentId: string;
    application?: LandownerPassthroughProgramApplication;
}

export interface LandownerProgramQueryResult extends LandownerPassthroughProgramApplication {
    wellStatus: string;
    wellDisposition: string;
}


export interface LandownerProgramSearchSpec {
    apiNumbers: string[];
    applicationId?: string;
    programStatus: LandownerProgramStatus[];
    landowner: string;
    contractor: string;
    includeStatusAndDisposition: boolean;
    ensureInspectors?: boolean;
}

export interface LandownerProgramCompleteWellRequest {
    applicationId: string;
    wellId: string;
    pluggedDate?: Date;
    statusUpdate: LandownerProgramStatus;
}

export interface LandownerProgramCompleteWellResponse {
    applicationId: string;
    wellId: string;
    status: LandownerProgramStatus;
    updatedDate: Date;
}